/**
 * On-device usage model used to guess what the viewer will open next.
 *
 * It counts openings per project and nothing else — no names, no marks, no identifiers that
 * mean anything off this device — and it is never transmitted: the model lives in this
 * browser's local storage and is read only by the prefetcher. Its single job is deciding
 * which project's statistics to compute before they are asked for.
 */

import type { ProjectId } from '../../shared/types.js';

/** Storage key of the serialized model. */
const STORAGE_KEY = 'anwesenheit.usage-model.v1';

/** Openings older than this contribute half as much to a project's score. */
const HALF_LIFE_DAYS = 14;

/** Largest number of projects kept; the least recently opened are pruned beyond it. */
const MAX_TRACKED_PROJECTS = 50;

/** Milliseconds in a day, used by the recency decay. */
const DAY_MS = 86_400_000;

/** What is remembered about one project. */
interface ProjectUsage {
  opens: number;
  lastOpenedAt: number;
}

/** The whole model. */
interface UsageModel {
  byProject: Record<ProjectId, ProjectUsage>;
}

/** Returns an empty model. */
function emptyModel(): UsageModel {
  return { byProject: {} };
}

/** Reads one stored usage entry, or `null` when it is malformed. */
function decodeUsage(value: unknown): ProjectUsage | null {
  if (typeof value !== 'object' || value === null) return null;
  const record = value as Record<string, unknown>;
  if (typeof record.opens !== 'number' || typeof record.lastOpenedAt !== 'number') return null;
  if (!Number.isFinite(record.opens) || !Number.isFinite(record.lastOpenedAt)) return null;
  return { opens: record.opens, lastOpenedAt: record.lastOpenedAt };
}

/** Loads the model from local storage, returning an empty one when absent or unreadable. */
function load(): UsageModel {
  const raw = globalThis.localStorage?.getItem(STORAGE_KEY) ?? null;
  if (raw === null) return emptyModel();
  try {
    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== 'object' || parsed === null) return emptyModel();
    const byProject = (parsed as Record<string, unknown>).byProject;
    if (typeof byProject !== 'object' || byProject === null) return emptyModel();
    const model = emptyModel();
    for (const [projectId, value] of Object.entries(byProject)) {
      const usage = decodeUsage(value);
      if (usage !== null) model.byProject[projectId] = usage;
    }
    return model;
  } catch {
    return emptyModel();
  }
}

/** Writes the model back, dropping the least recently opened projects beyond the cap. */
function save(model: UsageModel): void {
  const entries = Object.entries(model.byProject)
    .sort(([, left], [, right]) => right.lastOpenedAt - left.lastOpenedAt)
    .slice(0, MAX_TRACKED_PROJECTS);
  const pruned: UsageModel = { byProject: Object.fromEntries(entries) };
  try {
    globalThis.localStorage?.setItem(STORAGE_KEY, JSON.stringify(pruned));
  } catch {
    // Storage can be full or blocked; the prefetcher degrades to no prediction, which is
    // only a performance loss, so there is nothing to recover here.
  }
}

/** Records that `projectId` was opened, at `at`. */
export function recordProjectOpen(projectId: ProjectId, at: number = Date.now()): void {
  const model = load();
  const usage = model.byProject[projectId] ?? { opens: 0, lastOpenedAt: at };
  model.byProject[projectId] = { opens: usage.opens + 1, lastOpenedAt: at };
  save(model);
}

/** Weight of an opening that happened `elapsedMs` ago, halving every `HALF_LIFE_DAYS`. */
function decay(elapsedMs: number): number {
  return 2 ** (-Math.max(0, elapsedMs) / (HALF_LIFE_DAYS * DAY_MS));
}

/**
 * Ranks `candidates` by how likely they are to be opened next.
 *
 * Projects with no history score zero and keep their incoming order, so a first-time user
 * gets the list unchanged rather than an arbitrary shuffle.
 *
 * @param limit largest number of projects to return.
 * @returns the highest-scoring project ids, most likely first.
 */
export function rankProjects(
  candidates: readonly ProjectId[],
  limit: number,
  now: number = Date.now(),
): ProjectId[] {
  const model = load();
  return candidates
    .map((projectId, index) => {
      const usage = model.byProject[projectId];
      const score = usage === undefined ? 0 : usage.opens * decay(now - usage.lastOpenedAt);
      return { projectId, score, index };
    })
    .sort((left, right) => (right.score - left.score) || (left.index - right.index))
    .slice(0, limit)
    .map((entry) => entry.projectId);
}

/** Erases the usage model from this device. */
export function clearUsageModel(): void {
  globalThis.localStorage?.removeItem(STORAGE_KEY);
}
